import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { productAPI, reviewAPI, productQuestionAPI, priceAlertAPI, IMAGE_BASE } from '../../services/api';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import useScrollReveal from '../../hooks/useScrollReveal';
import useSeo from '../../hooks/useSeo';
import { ArrowLeft, CheckCircle, XCircle, Package, ChevronLeft, ChevronRight, ShoppingBag, ShoppingCart, Check, Minus, Plus, X, Star, MessageSquare, ChevronDown, HelpCircle, Send, Clock, Info, Share2, Heart, ListPlus, BarChart3, Bell, Copy, ExternalLink } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useWishlist } from '../../context/WishlistContext';
import { useCompare } from '../../context/CompareContext';

export default function ProductDetail() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [selectedImage, setSelectedImage] = useState(0);
    const [loading, setLoading] = useState(true);
    const revealRef = useScrollReveal();
    const { addItem, loading: cartLoading } = useCart();
    const { customer } = useCustomerAuth();
    const { settings } = useSiteSettings();
    const navigate = useNavigate();
    const { toggleFavorite, isFavorited } = useFavorites();
    const { toggleWishlist, isInWishlist } = useWishlist();
    const { toggleCompare, isInCompare, compareCount, maxCompare } = useCompare();
    const [qty, setQty] = useState(1);
    const [activeTab, setActiveTab] = useState('description');
    const [showPreOrderInfo, setShowPreOrderInfo] = useState(false);

    // Share state
    const [shareOpen, setShareOpen] = useState(false);
    const [copyMsg, setCopyMsg] = useState('');
    const shareRef = useRef(null);

    // Price alert state
    const [priceAlertOpen, setPriceAlertOpen] = useState(false);
    const [priceAlertEmail, setPriceAlertEmail] = useState('');
    const [priceAlertMsg, setPriceAlertMsg] = useState('');
    const [priceAlertLoading, setPriceAlertLoading] = useState(false);
    const [hasActiveAlert, setHasActiveAlert] = useState(false);

    // Zoom state
    const [zoomActive, setZoomActive] = useState(false);
    const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
    const [mobileModalOpen, setMobileModalOpen] = useState(false);
    const [mobileModalIndex, setMobileModalIndex] = useState(0);
    const galleryMainRef = useRef(null);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Lock body scroll when mobile modal is open
    useEffect(() => {
        if (mobileModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileModalOpen]);

    // Reviews state
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({ total: 0, avgRating: 0, distribution: [] });

    // Q&A state
    const [questions, setQuestions] = useState([]);
    const [questionText, setQuestionText] = useState('');
    const [questionLoading, setQuestionLoading] = useState(false);
    const [questionMsg, setQuestionMsg] = useState('');

    // Variant state
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRefs = useRef({});

    // Standalone size state (non-variant products)
    const [selectedSize, setSelectedSize] = useState(null);
    const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
    const sizeDropdownRef = useRef(null);

    // Close variant/size dropdown on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (openDropdown && dropdownRefs.current[openDropdown] && !dropdownRefs.current[openDropdown].contains(e.target)) {
                setOpenDropdown(null);
            }
            if (sizeDropdownOpen && sizeDropdownRef.current && !sizeDropdownRef.current.contains(e.target)) {
                setSizeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [openDropdown, sizeDropdownOpen]);

    // Close share dropdown on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (shareOpen && shareRef.current && !shareRef.current.contains(e.target)) {
                setShareOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [shareOpen]);

    // Pre-fill price alert email if logged in
    useEffect(() => {
        if (customer?.email) setPriceAlertEmail(customer.email);
    }, [customer]);

    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            setRelatedProducts([]);
            try {
                const res = await productAPI.getBySlug(slug);
                setProduct(res.data);
                // Initialize variant selection with first values
                if (res.data.variantOptions?.length > 0) {
                    const initial = {};
                    res.data.variantOptions.forEach(opt => {
                        initial[opt.name] = opt.values[0] || '';
                    });
                    setSelectedAttributes(initial);
                }
                // Initialize standalone size selection
                if (!res.data.hasVariants && res.data.sizes?.length > 0) {
                    setSelectedSize(res.data.sizes[0]);
                } else {
                    setSelectedSize(null);
                }
                // Fetch related products from same category
                if (res.data.categoryId) {
                    try {
                        const relRes = await productAPI.getAll({ category: res.data.categoryId, limit: 10 });
                        const others = (relRes.data.products || relRes.data || []).filter(p => p.id !== res.data.id);
                        setRelatedProducts(others.slice(0, 8));
                    } catch (e) { /* ignore */ }
                }
                // Fetch reviews & questions
                try {
                    const revRes = await reviewAPI.getProductReviews(res.data.id);
                    setReviews(revRes.data.reviews || []);
                    setReviewStats(revRes.data.stats || { total: 0, avgRating: 0, distribution: [] });
                } catch (e) { /* ignore */ }
                try {
                    const qRes = await productQuestionAPI.getProductQuestions(res.data.id);
                    setQuestions(qRes.data || []);
                } catch (e) { /* ignore */ }
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        fetchProduct();
        window.scrollTo(0, 0);
    }, [slug]);

    const getImageUrl = (imgObj) => {
        if (!imgObj) return '';
        return imgObj.url?.startsWith('http') ? imgObj.url : IMAGE_BASE + imgObj.url;
    };

    const VIDEO_EXTS = ['.mp4', '.webm', '.mov'];
    const isVideo = (item) => item?.mediaType === 'video' || VIDEO_EXTS.some(ext => (item?.url || '').toLowerCase().split('?')[0].endsWith(ext));

    const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

    // Find selected variant
    const selectedVariant = useMemo(() => {
        if (!product?.hasVariants || !product?.variants?.length) return null;
        const attrs = Object.entries(selectedAttributes);
        if (attrs.length === 0) return null;
        return product.variants.find(v => {
            const va = v.attributes || [];
            return attrs.every(([name, value]) => va.some(a => a.name === name && a.value === value));
        }) || null;
    }, [product, selectedAttributes]);

    // Determine which images to show
    const displayImages = useMemo(() => {
        if (selectedVariant?.images?.length > 0) {
            return selectedVariant.images;
        }
        return product?.images || [];
    }, [selectedVariant, product]);

    // Reset selected image when images change
    useEffect(() => {
        setSelectedImage(0);
    }, [displayImages]);

    const handleManualNav = (idx) => {
        setSelectedImage(idx);
    };

    // Determine display price, stock, sku
    const displayPrice = selectedVariant ? parseFloat(selectedVariant.price) : parseFloat(product?.price || 0);
    const displayComparePrice = selectedVariant ? (selectedVariant.comparePrice ? parseFloat(selectedVariant.comparePrice) : null) : (product?.comparePrice ? parseFloat(product.comparePrice) : null);
    const displayStock = selectedVariant ? selectedVariant.stock : (product?.stock || 0);
    const displaySku = selectedVariant?.sku || product?.sku;
    const discount = displayComparePrice ? Math.round((1 - displayPrice / displayComparePrice) * 100) : 0;

    const handleAskQuestion = async () => {
        if (!questionText.trim()) return;
        setQuestionLoading(true);
        try {
            await productQuestionAPI.create({ productId: product.id, question: questionText.trim() });
            setQuestionText('');
            setQuestionMsg('Sorunuz gönderildi! Cevaplanınca burada yayınlanacaktır.');
            setTimeout(() => setQuestionMsg(''), 4000);
        } catch (e) {
            setQuestionMsg(e.response?.data?.error || 'Soru gönderilemedi');
            setTimeout(() => setQuestionMsg(''), 3000);
        }
        setQuestionLoading(false);
    };

    // Share helpers
    const productUrl = product ? `https://nooraprive.com/urun/${product.slug}` : '';
    const productShareText = product ? `${product.name} - Noora Privé` : '';

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(productUrl);
            setCopyMsg('Link kopyalandı!');
            setTimeout(() => setCopyMsg(''), 2000);
        } catch {
            setCopyMsg('Kopyalanamadı');
            setTimeout(() => setCopyMsg(''), 2000);
        }
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: productShareText, url: productUrl });
            } catch { /* user cancelled */ }
        }
    };

    // Favorite handler
    const handleToggleFavorite = async () => {
        if (!customer) {
            window.location.href = '/giris';
            return;
        }
        await toggleFavorite(product.id);
    };

    // Price alert handler
    const handlePriceAlert = async () => {
        if (!priceAlertEmail.trim() || !priceAlertEmail.includes('@')) {
            setPriceAlertMsg('Geçerli bir e-posta adresi girin');
            return;
        }
        setPriceAlertLoading(true);
        try {
            const res = await priceAlertAPI.create({
                email: priceAlertEmail.trim(),
                productId: product.id,
                variantId: selectedVariant?.id || null,
                priceWhenSet: displayPrice,
                customerId: customer?.id || null
            });
            if (res.data.alreadyExists) {
                setPriceAlertMsg('Bu ürün için zaten alarmınız var');
            } else {
                setPriceAlertMsg('Fiyat düşünce size haber vereceğiz!');
                setHasActiveAlert(true);
            }
            setTimeout(() => { setPriceAlertMsg(''); setPriceAlertOpen(false); }, 2500);
        } catch {
            setPriceAlertMsg('Bir hata oluştu, tekrar deneyin');
        }
        setPriceAlertLoading(false);
    };

    // Get first image URL for SEO
    const primaryImage = useMemo(() => {
        const img = product?.images?.find(i => i.isPrimary) || product?.images?.[0];
        if (!img) return null;
        return img.url?.startsWith('http') ? img.url : 'https://nooraprive.com' + img.url;
    }, [product]);

    useSeo(product ? {
        title: `${product.name} | ${product.category?.name || 'Kadın Giyim'} | Noora Privé`,
        description: product.shortDescription || `${product.name} - Noora Privé ${product.category?.name || 'kadın giyim'} koleksiyonu. Kaliteli kumaş, şık tasarım. Hızlı kargo, güvenli ödeme.`,
        keywords: `${product.name}, ${product.category?.name || ''}, kadın giyim, Noora Privé, nooraprive, online butik, butik kadın giyim, online alışveriş, hızlı kargo`,
        canonical: `https://nooraprive.com/urun/${product.slug}`,
        image: primaryImage
    } : {});

    return (
        <div className="product-detail" ref={revealRef}>
            {loading ? (
                <div className="loading"><div className="spinner"></div></div>
            ) : !product ? (
                <div className="empty-state">
                    <XCircle size={48} strokeWidth={1.5} />
                    <h3>Ürün bulunamadı</h3>
                    <Link to="/urunler" className="btn btn-primary" style={{ marginTop: '1rem' }}>Ürünlere Dön</Link>
                </div>
            ) : (
            <>
            <nav className="pd-breadcrumb">
                <Link to="/">Ana Sayfa</Link>
                <ChevronRight size={14} />
                <Link to="/urunler">Ürünler</Link>
                {product.category?.parent?.parent && (
                    <>
                        <ChevronRight size={14} />
                        <Link to={`/urunler?category=${product.category.parent.parent.id}`}>{product.category.parent.parent.name}</Link>
                    </>
                )}
                {product.category?.parent && (
                    <>
                        <ChevronRight size={14} />
                        <Link to={`/urunler?category=${product.category.parent.id}`}>{product.category.parent.name}</Link>
                    </>
                )}
                {product.category && (
                    <>
                        <ChevronRight size={14} />
                        <Link to={`/urunler?category=${product.category.id}`}>{product.category.name}</Link>
                    </>
                )}
                <ChevronRight size={14} />
                <span>{product.name}</span>
            </nav>

            <div className="product-detail-grid reveal">
                {/* Gallery */}
                <div className="product-gallery">
                    <div
                        className={`product-gallery-main ${zoomActive ? 'zoom-active' : ''}`}
                        ref={galleryMainRef}
                        onMouseEnter={() => { if (!isMobile && !isVideo(displayImages[selectedImage])) setZoomActive(true); }}
                        onMouseLeave={() => setZoomActive(false)}
                        onMouseMove={(e) => {
                            if (!zoomActive || !galleryMainRef.current) return;
                            const rect = galleryMainRef.current.getBoundingClientRect();
                            const x = ((e.clientX - rect.left) / rect.width) * 100;
                            const y = ((e.clientY - rect.top) / rect.height) * 100;
                            setZoomPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
                        }}
                        onClick={() => {
                            if (isMobile && !isVideo(displayImages[selectedImage]) && displayImages.length > 0) {
                                setMobileModalIndex(selectedImage);
                                setMobileModalOpen(true);
                            }
                        }}
                    >
                        {displayImages.length > 0 ? (
                            <>
                                {isVideo(displayImages[selectedImage]) ? (
                                    <video
                                        key={selectedImage}
                                        src={getImageUrl(displayImages[selectedImage])}
                                        controls
                                        autoPlay
                                        muted
                                        playsInline
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <>
                                        <img src={getImageUrl(displayImages[selectedImage])} alt={product.name} draggable={false} />
                                        {zoomActive && (
                                            <div
                                                className="zoom-lens"
                                                style={{
                                                    left: `${zoomPos.x}%`,
                                                    top: `${zoomPos.y}%`,
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                                {displayImages.length > 1 && (
                                    <>
                                        <button className="gallery-nav gallery-nav-left" onClick={(e) => { e.stopPropagation(); handleManualNav(selectedImage === 0 ? displayImages.length - 1 : selectedImage - 1); }}>
                                            <ChevronLeft size={20} />
                                        </button>
                                        <button className="gallery-nav gallery-nav-right" onClick={(e) => { e.stopPropagation(); handleManualNav((selectedImage + 1) % displayImages.length); }}>
                                            <ChevronRight size={20} />
                                        </button>
                                    </>
                                )}
                            </>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
                                <Package size={64} strokeWidth={1} />
                            </div>
                        )}
                    </div>

                    {/* Desktop Zoom Panel */}
                    {zoomActive && displayImages[selectedImage] && !isVideo(displayImages[selectedImage]) && (
                        <div
                            className="zoom-result"
                            style={{
                                backgroundImage: `url(${getImageUrl(displayImages[selectedImage])})`,
                                backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
                            }}
                        />
                    )}
                    {displayImages.length > 1 && (
                        <div className="product-gallery-thumbs">
                            {displayImages.map((img, idx) => (
                                <div key={img.id || idx} className={`product-gallery-thumb ${selectedImage === idx ? 'active' : ''}`} onClick={() => handleManualNav(idx)}>
                                    {isVideo(img) ? (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)' }}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--accent-primary)' }}><polygon points="5,3 19,12 5,21" /></svg>
                                        </div>
                                    ) : (
                                        <img src={getImageUrl(img)} alt={img.alt || product.name} />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="product-info">
                    {product.category && (
                        <Link to={`/urunler?category=${product.category.id}`} className="category-link">{product.category.name}</Link>
                    )}
                    <h1>{product.name}</h1>
                    {displaySku && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>SKU: {displaySku}</p>}

                    <div className="product-price-section">
                        <span className="current">{formatPrice(displayPrice)}</span>
                        {displayComparePrice && <span className="compare">{formatPrice(displayComparePrice)}</span>}
                        {discount > 0 && <span className="discount">%{discount} İndirim</span>}
                    </div>

                    {/* Variant Selector */}
                    {product.hasVariants && product.variantOptions?.length > 0 && (
                        <div className="variant-selector">
                            {product.variantOptions.map(opt => {
                                const isOpen = openDropdown === opt.name;
                                const hasColors = opt.colorCodes && Object.keys(opt.colorCodes).length > 0;
                                const selectedColor = hasColors ? opt.colorCodes[selectedAttributes[opt.name]] : null;
                                return (
                                <div key={opt.name} className="variant-group" ref={el => dropdownRefs.current[opt.name] = el}>
                                    <button
                                        type="button"
                                        className={`variant-dropdown-trigger ${isOpen ? 'open' : ''}`}
                                        onClick={() => setOpenDropdown(isOpen ? null : opt.name)}
                                    >
                                        <span className="variant-dropdown-label">
                                            {opt.name}
                                        </span>
                                        <span className="variant-dropdown-value">
                                            {hasColors && selectedColor && (
                                                <span className="variant-dropdown-color-dot" style={{ background: selectedColor }} />
                                            )}
                                            {selectedAttributes[opt.name] || 'Seçiniz'}
                                        </span>
                                        <ChevronDown size={16} className={`variant-dropdown-chevron ${isOpen ? 'open' : ''}`} />
                                    </button>
                                    {isOpen && (
                                        <div className="variant-dropdown-menu">
                                            {opt.values.map(val => {
                                                const isSelected = selectedAttributes[opt.name] === val;
                                                const testAttrs = { ...selectedAttributes, [opt.name]: val };
                                                const matchingVariant = product.variants.find(v => {
                                                    const va = v.attributes || [];
                                                    return Object.entries(testAttrs).every(([n, vl]) => va.some(a => a.name === n && a.value === vl));
                                                });
                                                const isAvailable = matchingVariant?.isActive !== false;
                                                const colorCode = hasColors ? opt.colorCodes[val] : null;
                                                return (
                                                    <button
                                                        key={val}
                                                        type="button"
                                                        className={`variant-dropdown-item ${isSelected ? 'selected' : ''} ${!isAvailable ? 'unavailable' : ''}`}
                                                        onClick={() => {
                                                            if (!isAvailable) return;
                                                            setSelectedAttributes(prev => ({ ...prev, [opt.name]: val }));
                                                            setOpenDropdown(null);
                                                        }}
                                                    >
                                                        {colorCode && (
                                                            <span className="variant-dropdown-item-dot" style={{ background: colorCode }} />
                                                        )}
                                                        <span>{val}</span>
                                                        {isSelected && <Check size={15} />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Standalone Size Selector (non-variant products) */}
                    {!product.hasVariants && product.sizes?.length > 0 && (
                        <div className="variant-selector">
                            <div className="variant-group" ref={sizeDropdownRef}>
                                <button
                                    type="button"
                                    className={`variant-dropdown-trigger ${sizeDropdownOpen ? 'open' : ''}`}
                                    onClick={() => setSizeDropdownOpen(!sizeDropdownOpen)}
                                >
                                    <span className="variant-dropdown-label">Beden</span>
                                    <span className="variant-dropdown-value">
                                        {selectedSize || 'Seçiniz'}
                                    </span>
                                    <ChevronDown size={16} className={`variant-dropdown-chevron ${sizeDropdownOpen ? 'open' : ''}`} />
                                </button>
                                {sizeDropdownOpen && (
                                    <div className="variant-dropdown-menu">
                                        {product.sizes.map(size => (
                                            <button
                                                key={size}
                                                type="button"
                                                className={`variant-dropdown-item ${selectedSize === size ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setSelectedSize(size);
                                                    setSizeDropdownOpen(false);
                                                }}
                                            >
                                                <span>{size}</span>
                                                {selectedSize === size && <Check size={15} />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!product.isPreOrder && (
                        <div className="product-stock-info">
                            {displayStock > 0 ? (
                                <CheckCircle size={16} color="var(--success)" />
                            ) : (
                                <XCircle size={16} color="var(--danger)" />
                            )}
                            <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                {displayStock === 0 ? 'Stokta Yok' : displayStock <= (product.minStock || 5) ? 'Sınırlı Stok' : 'Stokta Var'}
                            </span>
                        </div>
                    )}

                    {/* Pre-Order Badge & Info */}
                    {product.isPreOrder && (
                        <div style={{ marginBottom: '1rem' }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px',
                                background: 'linear-gradient(135deg, #fff5f5, #ffe8e8)', border: '1.5px solid #e74c3c',
                                borderRadius: '10px', marginBottom: '10px'
                            }}>
                                <Clock size={18} color="#e74c3c" />
                                <div style={{ flex: 1 }}>
                                    <span style={{ fontWeight: 700, color: '#e74c3c', fontSize: '0.9rem', display: 'block' }}>Ön Sipariş</span>
                                    {(product.preOrderShipStartDate || product.preOrderShipEndDate) && (
                                        <span style={{ fontSize: '0.82rem', color: '#555', marginTop: '2px', display: 'block' }}>
                                            Tahmini Kargo: {product.preOrderShipStartDate && new Date(product.preOrderShipStartDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
                                            {product.preOrderShipStartDate && product.preOrderShipEndDate && ' – '}
                                            {product.preOrderShipEndDate && new Date(product.preOrderShipEndDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => setShowPreOrderInfo(true)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    background: 'none', border: '1px solid #ccc', borderRadius: '20px',
                                    padding: '6px 14px', cursor: 'pointer', fontSize: '0.8rem',
                                    color: '#555', transition: 'all 0.2s'
                                }}
                                onMouseOver={e => { e.currentTarget.style.borderColor = '#e74c3c'; e.currentTarget.style.color = '#e74c3c'; }}
                                onMouseOut={e => { e.currentTarget.style.borderColor = '#ccc'; e.currentTarget.style.color = '#555'; }}
                            >
                                <Info size={14} /> Ön Sipariş Nedir?
                            </button>
                        </div>
                    )}

                    {product.shortDescription && (
                        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.5rem' }}>{product.shortDescription}</p>
                    )}

                    {/* Sepete Ekle */}
                    {displayStock > 0 && (
                        <div className="product-order-box">
                            <div className="product-order-row">
                                <div className="product-qty-control">
                                    <button onClick={() => setQty(q => Math.max(1, q - 1))} disabled={qty <= 1}><Minus size={16} /></button>
                                    <span>{qty}</span>
                                    <button onClick={() => setQty(q => Math.min(displayStock, q + 1))} disabled={qty >= displayStock}><Plus size={16} /></button>
                                </div>
                                <button
                                    className="product-add-cart-btn"
                                    disabled={cartLoading}
                                    onClick={() => addItem(product.id, selectedVariant?.id || null, qty, selectedSize)}
                                >
                                    <ShoppingBag size={18} />
                                    <span>{cartLoading ? 'Ekleniyor...' : 'Sepete Ekle'}</span>
                                </button>
                            </div>
                            <button
                                className="product-buy-now-btn"
                                disabled={cartLoading}
                                onClick={async () => {
                                    const ok = await addItem(product.id, selectedVariant?.id || null, qty, selectedSize);
                                    if (ok) navigate('/checkout?guest=true');
                                }}
                            >
                                Hemen Al
                            </button>
                            <a
                                href={(() => {
                                    const num = settings?.whatsapp || '905521593255';
                                    const variantInfo = selectedVariant
                                        ? '\nSeçilen Varyant: ' + (Array.isArray(selectedVariant.attributes) ? selectedVariant.attributes : JSON.parse(selectedVariant.attributes || '[]')).map(a => `${a.name}: ${a.value}`).join(', ')
                                        : '';
                                    const sizeInfo = selectedSize ? `\nBeden: ${selectedSize}` : '';
                                    const msg = `Merhaba, şu ürün hakkında bilgi almak istiyorum:\n\n*${product.name}*\nFiyat: ₺${displayPrice.toFixed(2)}${variantInfo}${sizeInfo}\n${productUrl}`;
                                    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
                                })()}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    width: '100%', padding: '13px', borderRadius: '10px', marginTop: '10px',
                                    background: '#25D366', color: '#fff', fontWeight: 600, fontSize: '0.95rem',
                                    textDecoration: 'none', border: 'none', cursor: 'pointer',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#1ebe5d'}
                                onMouseLeave={e => e.currentTarget.style.background = '#25D366'}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                WhatsApp ile Sor
                            </a>
                            <a
                                href={`tel:${(settings?.phone || '+905521593255').replace(/\s/g, '')}`}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                                    width: '100%', padding: '10px', borderRadius: '10px', marginTop: '8px',
                                    background: 'transparent', color: 'var(--text-secondary)',
                                    fontWeight: 500, fontSize: '0.85rem', textDecoration: 'none',
                                    border: '1px solid var(--border)', cursor: 'pointer',
                                    transition: 'border-color 0.2s, color 0.2s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--text-primary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .999h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                                Telefonla Sipariş Et
                            </a>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="pd-action-buttons">
                        <button
                            className={`pd-action-btn ${isFavorited(product.id) ? 'active' : ''}`}
                            onClick={handleToggleFavorite}
                            title="Favorilere Ekle"
                        >
                            <Heart size={17} fill={isFavorited(product.id) ? 'currentColor' : 'none'} />
                            <span>{isFavorited(product.id) ? 'Favorilerde' : 'Favorilere Ekle'}</span>
                        </button>

                        <button
                            className={`pd-action-btn ${isInWishlist(product.id) ? 'active' : ''}`}
                            onClick={() => toggleWishlist(product.id)}
                            title="İstek Listeme Ekle"
                        >
                            <ListPlus size={17} />
                            <span>{isInWishlist(product.id) ? 'İstek Listemde' : 'İstek Listeme Ekle'}</span>
                        </button>

                        <button
                            className={`pd-action-btn ${isInCompare(product.id) ? 'active' : ''}`}
                            onClick={() => {
                                if (!isInCompare(product.id) && compareCount >= maxCompare) {
                                    alert(`En fazla ${maxCompare} ürün karşılaştırabilirsiniz`);
                                    return;
                                }
                                toggleCompare(product.id);
                            }}
                            title="Karşılaştır"
                        >
                            <BarChart3 size={17} />
                            <span>{isInCompare(product.id) ? 'Karşılaştırmada' : 'Karşılaştır'}</span>
                        </button>

                        <button
                            className={`pd-action-btn ${hasActiveAlert ? 'active' : ''}`}
                            onClick={() => {
                                if (hasActiveAlert) return;
                                setPriceAlertOpen(true);
                            }}
                            title="Fiyat Düşünce Haber Ver"
                        >
                            <Bell size={17} />
                            <span>{hasActiveAlert ? 'Alarm Kuruldu' : 'Fiyat Düşünce Haber Ver'}</span>
                        </button>
                    </div>

                    {/* Share Button */}
                    <div className="pd-share-wrapper" ref={shareRef}>
                        <button className="pd-share-btn" onClick={() => {
                            if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
                                handleNativeShare();
                            } else {
                                setShareOpen(!shareOpen);
                            }
                        }}>
                            <Share2 size={16} /> Paylaş
                        </button>
                        {shareOpen && (
                            <div className="pd-share-dropdown">
                                <a
                                    href={`https://wa.me/?text=${encodeURIComponent(productShareText + ' ' + productUrl)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="pd-share-item"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                    WhatsApp
                                </a>
                                <a
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="pd-share-item"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                    Facebook
                                </a>
                                <a
                                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(productShareText)}&url=${encodeURIComponent(productUrl)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="pd-share-item"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                    X (Twitter)
                                </a>
                                <button className="pd-share-item" onClick={handleCopyLink}>
                                    <Copy size={18} />
                                    {copyMsg || 'Linki Kopyala'}
                                </button>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* Tabs */}
            <div className="pd-tabs">
                <div className="pd-tabs-nav">
                    <button className={`pd-tab-btn ${activeTab === 'description' ? 'active' : ''}`} onClick={() => setActiveTab('description')}>
                        <Package size={17} /> Açıklama
                    </button>
                    <button className={`pd-tab-btn ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => setActiveTab('reviews')}>
                        <Star size={17} /> Yorumlar {reviewStats.total > 0 && <span className="pd-tab-badge">{reviewStats.total}</span>}
                    </button>
                    <button className={`pd-tab-btn ${activeTab === 'qa' ? 'active' : ''}`} onClick={() => setActiveTab('qa')}>
                        <HelpCircle size={17} /> Soru & Cevap {questions.length > 0 && <span className="pd-tab-badge">{questions.length}</span>}
                    </button>
                </div>

                <div className="pd-tab-content">
                    {activeTab === 'description' && (
                        product.description ? (
                            <div className="pd-desc-content" dangerouslySetInnerHTML={{ __html: product.description }} />
                        ) : (
                            <div className="pd-reviews-empty">
                                <Package size={32} strokeWidth={1} />
                                <p>Ürün açıklaması henüz eklenmemiş</p>
                            </div>
                        )
                    )}

                    {activeTab === 'reviews' && (
                        reviewStats.total > 0 ? (
                            <>
                                <div className="pd-reviews-summary">
                                    <div className="pd-reviews-avg">
                                        <span className="pd-reviews-avg-num">{reviewStats.avgRating}</span>
                                        <div className="pd-reviews-avg-stars">
                                            {[1,2,3,4,5].map(s => (
                                                <Star key={s} size={18} fill={s <= Math.round(reviewStats.avgRating) ? 'var(--warning)' : 'none'} color={s <= Math.round(reviewStats.avgRating) ? 'var(--warning)' : 'var(--text-muted)'} />
                                            ))}
                                        </div>
                                        <span className="pd-reviews-avg-total">{reviewStats.total} değerlendirme</span>
                                    </div>
                                    <div className="pd-reviews-bars">
                                        {(reviewStats.distribution || []).map(d => (
                                            <div key={d.star} className="pd-reviews-bar-row">
                                                <span>{d.star}</span>
                                                <Star size={12} fill="var(--warning)" color="var(--warning)" />
                                                <div className="pd-reviews-bar-track">
                                                    <div className="pd-reviews-bar-fill" style={{ width: `${reviewStats.total ? (d.count / reviewStats.total) * 100 : 0}%` }} />
                                                </div>
                                                <span className="pd-reviews-bar-count">{d.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="pd-reviews-list">
                                    {reviews.map(review => (
                                        <div key={review.id} className="pd-review-card">
                                            <div className="pd-review-header">
                                                <div className="pd-review-avatar">
                                                    {review.customer?.firstName?.[0]}{review.customer?.lastName?.[0]}
                                                </div>
                                                <div>
                                                    <strong>{review.customer?.firstName} {review.customer?.lastName?.[0]}.</strong>
                                                    <div className="pd-review-stars">
                                                        {[1,2,3,4,5].map(s => (
                                                            <Star key={s} size={14} fill={s <= review.rating ? 'var(--warning)' : 'none'} color={s <= review.rating ? 'var(--warning)' : 'var(--text-muted)'} />
                                                        ))}
                                                    </div>
                                                </div>
                                                <span className="pd-review-date">{new Date(review.createdAt).toLocaleDateString('tr-TR')}</span>
                                            </div>
                                            <p className="pd-review-comment">{review.comment}</p>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="pd-reviews-empty">
                                <MessageSquare size={32} strokeWidth={1} />
                                <p>Henüz yorum yapılmamış</p>
                            </div>
                        )
                    )}

                    {activeTab === 'qa' && (
                        <div className="pd-qa-section">
                            <div className="pd-qa-form">
                                {customer ? (
                                    <>
                                        <h4>Bu ürün hakkında bir sorunuz mu var?</h4>
                                        {questionMsg && <p className="pd-qa-msg">{questionMsg}</p>}
                                        <div className="pd-qa-input-row">
                                            <input
                                                type="text"
                                                placeholder="Sorunuzu yazın..."
                                                value={questionText}
                                                onChange={e => setQuestionText(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter' && questionText.trim()) handleAskQuestion(); }}
                                                maxLength={500}
                                            />
                                            <button
                                                className="btn btn-primary pd-qa-send-btn"
                                                disabled={questionLoading || !questionText.trim()}
                                                onClick={handleAskQuestion}
                                            >
                                                <Send size={16} /> Gönder
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="pd-qa-login-prompt">
                                        <HelpCircle size={20} />
                                        <span>Soru sormak için <Link to="/giris">giriş yapın</Link></span>
                                    </div>
                                )}
                            </div>

                            {questions.length > 0 ? (
                                <div className="pd-qa-list">
                                    {questions.map(q => (
                                        <div key={q.id} className="pd-qa-card">
                                            <div className="pd-qa-question">
                                                <div className="pd-qa-icon pd-qa-icon-q">S</div>
                                                <div className="pd-qa-body">
                                                    <p>{q.question}</p>
                                                    <span className="pd-qa-meta">
                                                        {q.customer?.firstName} {q.customer?.lastName?.[0]}. - {new Date(q.createdAt).toLocaleDateString('tr-TR')}
                                                    </span>
                                                </div>
                                            </div>
                                            {q.answer && (
                                                <div className="pd-qa-answer">
                                                    <div className="pd-qa-icon pd-qa-icon-a">C</div>
                                                    <div className="pd-qa-body">
                                                        <p>{q.answer}</p>
                                                        <span className="pd-qa-meta">Satıcı</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="pd-reviews-empty">
                                    <HelpCircle size={32} strokeWidth={1} />
                                    <p>Henüz soru sorulmamış</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Accessories */}
            {product.accessories?.length > 0 && (
                <div className="acc-section">
                    <h2>Birlikte Alınan Ürünler</h2>
                    <div className="acc-grid">
                        {product.accessories.map(acc => {
                            const accImg = acc.images?.[0]?.url;
                            const accImgUrl = accImg ? (accImg.startsWith('http') ? accImg : IMAGE_BASE + accImg) : null;
                            const accPrice = parseFloat(acc.price || 0);
                            const accCompare = acc.comparePrice ? parseFloat(acc.comparePrice) : null;
                            const accDiscount = accCompare ? Math.round((1 - accPrice / accCompare) * 100) : 0;
                            return (
                                <div key={acc.id} className="acc-card">
                                    <Link to={`/urun/${acc.slug}`} className="acc-card-link">
                                        <div className="acc-card-img">
                                            {accImgUrl ? (
                                                <img src={accImgUrl} alt={acc.name} loading="lazy" />
                                            ) : (
                                                <Package size={28} strokeWidth={1} />
                                            )}
                                            {accDiscount > 0 && <span className="acc-discount">%{accDiscount}</span>}
                                        </div>
                                        <div className="acc-card-info">
                                            <h4>{acc.name}</h4>
                                            <div className="acc-card-price">
                                                <span className="current">{formatPrice(accPrice)}</span>
                                                {accCompare && <span className="compare">{formatPrice(accCompare)}</span>}
                                            </div>
                                        </div>
                                    </Link>
                                    <button
                                        className="acc-add-btn"
                                        title="Sepete Ekle"
                                        onClick={() => addItem(acc.id, null, 1)}
                                        disabled={cartLoading || acc.stock <= 0}
                                    >
                                        <Plus size={18} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Related Products */}
            {relatedProducts.length > 0 && (
                <div className="related-products">
                    <h2>Benzer Ürünler</h2>
                    <div className="related-products-grid">
                        {relatedProducts.map(rp => {
                            const rpImg = rp.images?.[0]?.url;
                            const rpImgUrl = rpImg ? (rpImg.startsWith('http') ? rpImg : IMAGE_BASE + rpImg) : null;
                            const rpPrice = parseFloat(rp.price || 0);
                            const rpCompare = rp.comparePrice ? parseFloat(rp.comparePrice) : null;
                            const rpDiscount = rpCompare ? Math.round((1 - rpPrice / rpCompare) * 100) : 0;
                            return (
                                <Link to={`/urun/${rp.slug}`} key={rp.id} className="related-product-card">
                                    <div className="related-product-img">
                                        {rpImgUrl ? (
                                            <img src={rpImgUrl} alt={rp.name} loading="lazy" />
                                        ) : (
                                            <Package size={32} strokeWidth={1} />
                                        )}
                                        {rpDiscount > 0 && <span className="related-product-discount">%{rpDiscount}</span>}
                                    </div>
                                    <div className="related-product-info">
                                        <h4>{rp.name}</h4>
                                        <div className="related-product-price">
                                            <span className="current">{formatPrice(rpPrice)}</span>
                                            {rpCompare && <span className="compare">{formatPrice(rpCompare)}</span>}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* Mobile Fullscreen Modal */}
            {mobileModalOpen && (
                <div className="mobile-zoom-overlay" onClick={() => setMobileModalOpen(false)}>
                    <button className="mobile-zoom-close" onClick={() => setMobileModalOpen(false)}>
                        <X size={24} />
                    </button>
                    <div className="mobile-zoom-counter">
                        {mobileModalIndex + 1} / {displayImages.filter(img => !isVideo(img)).length}
                    </div>
                    <div className="mobile-zoom-img-wrap" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={getImageUrl(displayImages[mobileModalIndex])}
                            alt={product.name}
                            draggable={false}
                        />
                    </div>
                    {displayImages.length > 1 && (
                        <>
                            <button className="mobile-zoom-nav mobile-zoom-nav-left" onClick={(e) => { e.stopPropagation(); setMobileModalIndex(mobileModalIndex === 0 ? displayImages.length - 1 : mobileModalIndex - 1); }}>
                                <ChevronLeft size={28} />
                            </button>
                            <button className="mobile-zoom-nav mobile-zoom-nav-right" onClick={(e) => { e.stopPropagation(); setMobileModalIndex((mobileModalIndex + 1) % displayImages.length); }}>
                                <ChevronRight size={28} />
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Price Alert Modal */}
            {priceAlertOpen && (
                <div className="pd-modal-overlay" onClick={() => setPriceAlertOpen(false)}>
                    <div className="pd-price-alert-modal" onClick={e => e.stopPropagation()}>
                        <div className="pd-price-alert-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Bell size={20} />
                                <h3>Fiyat Düşünce Haber Ver</h3>
                            </div>
                            <button className="pd-modal-close" onClick={() => setPriceAlertOpen(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="pd-price-alert-body">
                            <p>Bu ürünün fiyatı düştüğünde size e-posta ile bildirim gönderelim.</p>
                            <div className="pd-price-alert-current">
                                <span>Mevcut Fiyat:</span>
                                <strong>{formatPrice(displayPrice)}</strong>
                            </div>
                            <div className="pd-price-alert-input">
                                <input
                                    type="email"
                                    placeholder="E-posta adresiniz"
                                    value={priceAlertEmail}
                                    onChange={e => setPriceAlertEmail(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handlePriceAlert(); }}
                                />
                                <button
                                    className="btn btn-primary"
                                    onClick={handlePriceAlert}
                                    disabled={priceAlertLoading}
                                >
                                    {priceAlertLoading ? 'Kaydediliyor...' : 'Alarm Kur'}
                                </button>
                            </div>
                            {priceAlertMsg && <p className="pd-price-alert-msg">{priceAlertMsg}</p>}
                        </div>
                    </div>
                </div>
            )}

            {/* Pre-Order Info Popup */}
            {showPreOrderInfo && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '1rem', animation: 'fadeIn 0.25s ease'
                }} onClick={() => setShowPreOrderInfo(false)}>
                    <div style={{
                        background: '#fff', borderRadius: '20px', maxWidth: '560px', width: '100%',
                        maxHeight: '85vh', overflow: 'hidden', position: 'relative',
                        boxShadow: '0 25px 80px rgba(0,0,0,0.25)', animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)'
                    }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div style={{
                            background: '#fff', padding: '1.5rem 2rem 1rem',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            borderBottom: '1px solid #f0f0f0', position: 'relative'
                        }}>
                            <button onClick={() => setShowPreOrderInfo(false)} style={{
                                position: 'absolute', top: '1rem', right: '1rem',
                                background: '#f5f5f5', border: 'none', borderRadius: '50%',
                                width: '34px', height: '34px', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', cursor: 'pointer', transition: 'background 0.2s'
                            }}
                            onMouseOver={e => e.currentTarget.style.background = '#e8e8e8'}
                            onMouseOut={e => e.currentTarget.style.background = '#f5f5f5'}>
                                <X size={16} color="#333" />
                            </button>
                            <img src="/logo_noora.png" alt="Noora" style={{ height: '60px', objectFit: 'contain', marginBottom: '0.75rem' }} />
                            <div style={{ width: '40px', height: '1px', background: '#ccc', marginBottom: '0.75rem' }} />
                            <h3 style={{ margin: 0, fontSize: '1rem', color: '#333', fontWeight: 600, letterSpacing: '0.05em' }}>Ön Sipariş Nedir?</h3>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '1.5rem 2rem 2rem', overflowY: 'auto', maxHeight: 'calc(85vh - 160px)' }}>
                            <p style={{ color: '#444', fontSize: '0.8rem', lineHeight: 1.7, margin: '0 0 0.75rem' }}>
                                Siz değerli müşterilerimizin talepleri doğrultusunda ürünlerimize göstermiş olduğunuz ilginin karşılığı olarak birçok üründe ek stoklar açmaktayız. Ürün bazlı olarak değişiklik gösteren bu durumda sıfırdan sadece sizler için üretilen birçok ürünümüz mevcuttur. Üretici firma tarafından üretim için belirlenen süreye ek olarak ürünlerin nakliye süreleri de bu zaman dilimine eklenerek tahmini teslim süreleri oluşturulmaktadır. Tahmini teslim süresi ürünlerin işletmemize ulaşma süreleridir; ürünler tarafımıza ulaştıktan sonra aynı gün içerisinde paketleme işlemlerine başlanmakta ve kargo firmasına teslimatı sağlanmaktadır.
                            </p>

                            <p style={{ color: '#444', fontSize: '0.8rem', lineHeight: 1.7, margin: '0 0 0.75rem' }}>
                                Kargo firmaları gerçekleştirdikleri teslimat sonrasında sisteme işlenen takip numaraları için bilgilendirme SMS'lerini müşterilerimize göndermektedir. Ön sipariş bilgilerinin tamamı web sitemizde ürün görsel yanlarında (mobil üzerinden giriş yapıyorsak aşağı kaydırarak) bulunmaktadır.
                            </p>

                            <p style={{ color: '#444', fontSize: '0.8rem', lineHeight: 1.7, margin: '0 0 0.75rem' }}>
                                <strong>Hesaplama Örneği:</strong> 01.08.2024 tarihinde satın alınan bir ürün 7 günlük bir ön sipariş süresine sahipse bu durumda yapılması gereken hesaplama 01 Ağustos + 7 gün şeklinde olacaktır (yukarıda belirtilmiş olduğu gibi ürünün üretim ve nakliye süreçleri sonrası tarafımıza ulaşma süresidir).
                            </p>

                            <p style={{ color: '#444', fontSize: '0.8rem', lineHeight: 1.7, margin: '0 0 0.75rem' }}>
                                Ön sipariş ürün bilgilerinin sisteme kayıt edilmesi otomatik olarak Instagram hesabımız ile koordineli bir şekilde sağlanmaktadır. Ürün ön siparişe girmeden satın alan müşterilerimiz için işletmemizde bulunan stoklar bekletilmeden anında kargo çıkışları sağlanmaktadır.
                            </p>

                            <p style={{ color: '#444', fontSize: '0.8rem', lineHeight: 1.7, margin: 0 }}>
                                Bir sepet içerisinde ön sipariş bulunan ürünlerin mevcut olması durumunda (birden fazla ürün alımı yapılan durumlar için geçerlidir) tüm sepette bulunan ürünler aynı anda kargoya teslim edilmektedir.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            </>
            )}
        </div>
    );
}
